// Mermaid diagrams in rendered Markdown, drawn as GitHub draws them: a
// ```mermaid fenced block (marked writes `<pre><code class="language-mermaid">`,
// GitHub's HTML `<pre lang="mermaid">`) shows as its diagram, with its source
// folded under it. The mermaid library is loaded only once such a block is
// shown, so the app's bundle doesn't carry it.
//
// The source is a comment's, so untrusted. Mermaid runs at securityLevel
// `strict` with SVG text labels (no HTML labels), and a diagram may not
// loosen that, or restyle anything, with an init directive (`secure`). Its
// SVG is then shown as an image (`<img src="data:image/svg+xml,…">`), never
// put in the page: an SVG image runs no script, loads nothing, and its
// stylesheet styles only itself.
import type { Mermaid } from 'mermaid';

/** The fenced blocks drawn as diagrams. */
export const MERMAID_BLOCKS = 'pre > code.language-mermaid, pre[lang="mermaid"]';

/** The configuration keys a diagram's own `%%{init: …}%%` may not change. */
const SECURE = ['secure', 'securityLevel', 'startOnLoad', 'maxTextSize', 'suppressErrorRendering', 'maxEdges', 'htmlLabels', 'flowchart', 'theme', 'themeVariables', 'themeCSS', 'fontFamily', 'darkMode'];

export type MermaidLoader = () => Promise<{ default: Pick<Mermaid, 'initialize' | 'render'> }>;
const loadMermaid: MermaidLoader = () => import('mermaid');

/** The `<pre>` of each mermaid block under `root` not drawn or being drawn. */
export function mermaidBlocks(root: ParentNode): HTMLPreElement[] {
  const pres = [...root.querySelectorAll(MERMAID_BLOCKS)].map((el) => (el.tagName === 'PRE' ? el : el.parentElement) as HTMLPreElement);
  return [...new Set(pres)].filter((pre) => !pre.closest('.md-mermaid') && !pre.classList.contains('md-mermaid-pending'));
}

/** Whether the page shows the dark theme: its mark (theme.ts), else the system's. */
function darkTheme(): boolean {
  const mark = document.documentElement.dataset.theme;
  if (mark) return mark === 'dark';
  return typeof matchMedia === 'function' && matchMedia('(prefers-color-scheme: dark)').matches;
}

/** The SVG's drawn size, from its viewBox (mermaid sizes it to its container otherwise). */
function svgSize(svg: string): { width: number; height: number } | null {
  const box = new DOMParser().parseFromString(svg, 'image/svg+xml').documentElement.getAttribute('viewBox')?.trim().split(/[\s,]+/).map(Number);
  if (!box || box.length !== 4 || !box.every(Number.isFinite) || box[2] <= 0 || box[3] <= 0) return null;
  return { width: Math.ceil(box[2]), height: Math.ceil(box[3]) };
}

// Mermaid keeps one configuration and measures in the document: one drawing at a time.
let queue: Promise<unknown> = Promise.resolve();
let drawn = 0;

/** Draws `source`, or says why it can't (mermaid's message, first line). */
async function draw(load: MermaidLoader, source: string): Promise<{ svg: string } | { error: string }> {
  const run = queue.then(async () => {
    try {
      const { default: mermaid } = await load();
      mermaid.initialize({ startOnLoad: false, securityLevel: 'strict', htmlLabels: false, flowchart: { htmlLabels: false }, suppressErrorRendering: true, theme: darkTheme() ? 'dark' : 'default', secure: SECURE });
      return { svg: (await mermaid.render(`md-mermaid-${++drawn}`, source)).svg };
    } catch (e) {
      const message = (e instanceof Error ? e.message : String(e)).split('\n')[0].trim();
      return { error: message || 'It isn’t a diagram mermaid can read.' };
    }
  });
  queue = run;
  return run;
}

/** `pre`'s block as its diagram: the image, then the source, folded. */
function figure(pre: HTMLPreElement, source: string, svg: string): HTMLElement {
  const fig = document.createElement('figure');
  fig.className = 'md-mermaid';
  const img = document.createElement('img');
  img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  img.alt = `Diagram: ${source.trim().split('\n')[0].slice(0, 80)}`;
  const size = svgSize(svg);
  if (size) { img.width = size.width; img.height = size.height; }
  const details = document.createElement('details');
  const summary = document.createElement('summary');
  summary.textContent = 'Diagram source';
  details.append(summary);
  fig.append(img, details);
  pre.replaceWith(fig);
  details.append(pre);
  return fig;
}

/**
 * Draws every mermaid block under `root` as its diagram; one mermaid can't
 * read stays a code block, with a note saying why. Returns the stop: a
 * drawing that finishes after it, or after its block left the page, is
 * dropped.
 */
export function drawMermaid(root: ParentNode, load: MermaidLoader = loadMermaid): () => void {
  let stopped = false;
  for (const pre of mermaidBlocks(root)) {
    pre.classList.add('md-mermaid-pending');
    const source = pre.textContent ?? '';
    void draw(load, source).then((out) => {
      pre.classList.remove('md-mermaid-pending');
      if (stopped || !pre.isConnected) return;
      if ('svg' in out) { figure(pre, source, out.svg); return; }
      const note = document.createElement('p');
      note.className = 'md-mermaid-error';
      note.textContent = `This diagram couldn’t be drawn: ${out.error}`;
      pre.after(note);
    });
  }
  return () => { stopped = true; };
}
